import { useCallback, useEffect, useRef, useState } from "react";
import { GiftFilter } from "../../../services/gift.services";
import { initialPagination, PaginatedResponse } from "../../../domain/models/paginated-response.model";
import { CouponModel, getCouponPaginated, switchCoupon } from "../../../services/coupon/coupon.services";
import { handlePromise } from "../../../utils/handlePromisse";
import { CouponFormHandle } from "./form/coupon-form";
import { getValidity } from "./coupon-format";

type StatusFilter = "all" | "valid" | "expired";

/** Estado, fetch e handlers da página de cupons. */
export function useCouponPage() {
    const [loading, setLoading] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [filter, setFilter] = useState<GiftFilter>({ page: 1, size: 10 });
    const [coupons, setCoupons] = useState<PaginatedResponse<CouponModel>>(initialPagination);
    const [modalOpen, setModalOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
    const [selectedCoupon, setSelectedCoupon] = useState<CouponModel | undefined>(undefined);

    const formRef = useRef<CouponFormHandle>(null);

    const fetchCoupons = useCallback(async () => {
        setLoading(true);
        setCoupons(initialPagination);
        const [result, error] = await handlePromise(getCouponPaginated(filter));
        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(error.response.data.message);
            setLoading(false);
            return;
        }
        setCoupons(result.data as PaginatedResponse<CouponModel>);
        setLoading(false);
    }, [filter]);

    const handlePageChange = useCallback((newPage: number) => {
        setFilter((prev) => ({ ...prev, page: newPage }));
    }, []);

    const handlePageSizeChange = useCallback((newSize: number) => {
        setFilter((prev) => ({ ...prev, size: newSize, page: 1 }));
    }, []);

    const handleSwitchCoupon = useCallback(
        async (coupon: CouponModel) => {
            const [_, error] = await handlePromise(switchCoupon(coupon));
            if (!!error) {
                setShowSnackbar(true);
                setSnackbarMessage(error.response.data.message);
                return;
            }
            await fetchCoupons();
        },
        [fetchCoupons]
    );

    const handleCouponSaved = useCallback(async () => {
        setModalOpen(false);
        setSelectedCoupon(undefined);
        await fetchCoupons();
    }, [fetchCoupons]);

    const handleOpenCreate = useCallback(() => {
        setSelectedCoupon(undefined);
        setModalOpen(true);
    }, []);

    const handleOpenEdit = useCallback((coupon: CouponModel) => {
        setSelectedCoupon(coupon);
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setSelectedCoupon(undefined);
    }, []);

    useEffect(() => {
        fetchCoupons();
    }, [fetchCoupons, filter]);

    const displayedCoupons = coupons.data.filter((c) => {
        const matchesSearch = !search || c.code.toLowerCase().includes(search.toLowerCase());
        const validity = getValidity(c);
        const matchesStatus =
            statusFilter === "all" ||
            (statusFilter === "valid" && validity === "Válido") ||
            (statusFilter === "expired" && validity === "Expirado");
        return matchesSearch && matchesStatus;
    });

    return {
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        coupons,
        modalOpen,
        search,
        setSearch,
        statusFilter,
        setStatusFilter,
        selectedCoupon,
        formRef,
        displayedCoupons,
        handlePageChange,
        handlePageSizeChange,
        handleSwitchCoupon,
        handleCouponSaved,
        handleOpenCreate,
        handleOpenEdit,
        handleCloseModal,
    };
}
